import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import StateSelect from '@/components/StateSelect';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Briefcase, CheckCircle, User, Building2, Phone, Mail, MapPin, Lock, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cleanPhone } from '@/utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterServiceProvider = () => {
  const { agentId, providerRegKey } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validKey, setValidKey] = useState(providerRegKey ? null : true); // null = loading, true = valid, false = invalid
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    phone: '',
    email: '',
    city: '',
    state: 'NJ'
  });

  useEffect(() => {
    fetchData();
    if (providerRegKey) {
      validateKey();
    }
  }, [agentId, providerRegKey]);

  const validateKey = async () => {
    try {
      await axios.get(`${API}/site-settings/validate-provider-key/${providerRegKey}`);
      setValidKey(true);
    } catch {
      setValidKey(false);
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/public/service-types`);
      setServiceTypes(res.data || []);
    } catch (error) {
      toast.error('Failed to load service types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (selectedServiceTypes.length === 0) {
      toast.error('Please select at least one service type');
      return;
    }
    
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        email: formData.email.toLowerCase().trim(),
        phone: cleanPhone(formData.phone),
        service_types: selectedServiceTypes,
        password: password,
        ...(providerRegKey
          ? { provider_registration_key: providerRegKey }
          : { agent_id: agentId })
      };
      
      await axios.post(`${API}/public/service-providers`, submitData);

      setSubmitted(true);
      toast.success('Registration successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register. Please try again.');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (validKey === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
          <p className="mt-4 text-stone-600">Validating registration link...</p>
        </div>
      </div>
    );
  }

  if (validKey === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white p-8 rounded-2xl shadow-2xl text-center">
          <ShieldAlert className="mx-auto mb-4 text-red-500" size={64} />
          <h1 className="text-2xl font-bold text-stone-900 mb-2" style={{fontFamily: '"Playfair Display", serif'}}>Invalid Registration Link</h1>
          <p className="text-stone-600 mb-6">This registration link is invalid or has expired. Please contact the administrator for a valid link.</p>
          <Button onClick={() => navigate('/')} className="bg-[#1A4D2E] text-white rounded-full px-8" data-testid="go-home-btn">
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white p-8 rounded-2xl shadow-2xl text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={48} />
          </div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-4" style={{fontFamily: '"Playfair Display", serif'}}>
            Registration Complete!
          </h1>
          <p className="text-stone-600 mb-6">
            Thank you for registering. You can now login with your email and password!
          </p>
          <div className="bg-[#1A4D2E]/5 rounded-lg p-4 mb-6">
            <p className="text-sm text-stone-700">
              <strong>What's next?</strong>
              <br />
              Login to your provider dashboard to view assigned leads and manage your work.
            </p>
          </div>
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#1A4D2E] hover:bg-[#15381F] text-white mb-4"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Button>
          <p className="text-xs text-stone-500">
            You can safely close this page.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full bg-white p-8 rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="text-[#1A4D2E]" size={32} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Service Provider Registration
          </h1>
          <p className="text-stone-600">
            Connect with real estate professionals and grow your business
          </p>
          {providerRegKey && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3 text-left" data-testid="provider-direct-banner">
              <p className="text-sm text-blue-800">
                You're registering as a <strong>Service Provider</strong> (independent registration)
              </p>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name */}
          <div>
            <Label htmlFor="full_name" className="flex items-center gap-2 mb-2">
              <User size={16} />
              Full Name *
            </Label>
            <Input
              id="full_name"
              name="full_name"
              required
              value={formData.full_name}
              onChange={handleChange}
              placeholder="John Doe"
              className="h-12"
              data-testid="provider-reg-fullname"
            />
          </div>

          {/* Company Name */}
          <div>
            <Label htmlFor="company_name" className="flex items-center gap-2 mb-2">
              <Building2 size={16} />
              Company Name *
            </Label>
            <Input
              id="company_name"
              name="company_name"
              required
              value={formData.company_name}
              onChange={handleChange}
              placeholder="ABC Services LLC"
              className="h-12"
              data-testid="provider-reg-company"
            />
          </div>

          {/* Service Types Multi-Select */}
          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Briefcase size={16} />
              Service Types * (Select all that apply)
            </Label>
            <div className="border border-stone-300 rounded-md p-3 max-h-56 overflow-y-auto bg-white">
              {serviceTypes.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {serviceTypes.map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer hover:bg-stone-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={selectedServiceTypes.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedServiceTypes([...selectedServiceTypes, type]);
                          } else {
                            setSelectedServiceTypes(selectedServiceTypes.filter(t => t !== type));
                          }
                        }}
                        className="w-4 h-4 text-[#1A4D2E] border-stone-300 rounded focus:ring-[#1A4D2E]"
                      />
                      <span className="text-sm text-stone-700">{type}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-stone-500 text-center py-2">Loading service types...</p>
              )}
            </div>
            {selectedServiceTypes.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedServiceTypes.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-[#1A4D2E]/10 text-[#1A4D2E] rounded-full text-xs font-medium">
                    {t}
                    <button type="button" onClick={() => setSelectedServiceTypes(selectedServiceTypes.filter(s => s !== t))} className="hover:text-red-600">x</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
              <Phone size={16} />
              Phone Number *
            </Label>
            <PhoneInput
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              className="h-12"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="flex items-center gap-2 mb-2">
              <Mail size={16} />
              Email Address *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              className="h-12"
              data-testid="provider-reg-email"
            />
          </div>

          {/* City and State */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city" className="flex items-center gap-2 mb-2">
                <MapPin size={16} />
                City *
              </Label>
              <Input
                id="city"
                name="city"
                required
                value={formData.city}
                onChange={handleChange}
                placeholder="Los Angeles"
                className="h-12"
                data-testid="provider-reg-city"
              />
            </div>
            <div>
              <Label htmlFor="state" className="flex items-center gap-2 mb-2">
                <MapPin size={16} />
                State *
              </Label>
              <StateSelect
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="Select State"
                required
              />
            </div>
          </div>

          {/* Password Section */}
          <div className="pt-4 border-t-2 border-stone-200">
            <h3 className="text-lg font-bold text-[#1A4D2E] mb-2">Set Your Password</h3>
            <p className="text-sm text-stone-600 mb-4">
              Create a password (minimum 8 characters) for logging into your provider dashboard
            </p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="password" className="flex items-center gap-2 mb-2">
                  <Lock size={16} />
                  Password *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="h-12 pr-12"
                    data-testid="provider-reg-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="flex items-center gap-2 mb-2">
                  <Lock size={16} />
                  Confirm Password *
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="h-12 pr-12"
                    data-testid="provider-reg-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-14 bg-[#1A4D2E] text-white text-lg hover:bg-[#143D24] rounded-xl"
            data-testid="provider-reg-submit"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              'Submit Registration'
            )}
          </Button>

          <p className="text-xs text-center text-stone-500 mt-4">
            By submitting, you agree to be contacted by the agent/broker for potential business opportunities.
          </p>
        </form>
      </Card>
    </div>
  );
};

export default RegisterServiceProvider;
